import { Pipe, PipeTransform } from '@angular/core';

const SECONDS_IN_MINUTE = 60;
const SECONDS_IN_HOUR = 3600;

const isNumber = (value: string | number): value is number => typeof +value === 'number' && !isNaN(+value);
/**
 * @param value is a string with the format hh:mm:ss
 * @returns the time in seconds if is an invalid format will return 0
 */
const getTime = (value: string) => {
    const values = value.toString().split(':');
    if(values.length === 3){
        const [hours, minutes, seconds] = values.map(Number);
        // isNumber(value) return true or false if we add + in the begining we will have 1 or 0 if the user put 00:bb:23 the result will be 23 seconds
        return +isNumber(hours) * hours * SECONDS_IN_HOUR + +isNumber(minutes) * minutes * SECONDS_IN_MINUTE + +isNumber(seconds) * seconds;
    }
    return 0;
};

const getStandardSecond = (value: string | number) => {
    const isValidNumber = isNumber(value);
    if (!isValidNumber) return null;
    return +value;
};

const getStandardMinute = (value: string | number) => {
    const isValidNumber = isNumber(value);
    if (!isValidNumber) return null;
    return +value * SECONDS_IN_MINUTE;
};

const getStandardHour = (value: string | number) => {
    const isValidNumber = isNumber(value);
    if (!isValidNumber) return null;
    return +value * SECONDS_IN_HOUR;
};

const getStandardPercent = (value: string | number) => {
    const isValidNumber = isNumber(value);
    if (!isValidNumber) return null;
    return +value / 100;
};

const getStandardTime = (value: string | number) => {
    const isValidNumber = isNumber(value);
    return isValidNumber ? +value : getTime(value);
};

const getNumberOrZero = (value: string | number) => {
    const isValidNumber = isNumber(value);
    return isValidNumber ? +value : 0;
};

const getFormattedTime = (value: string | number) => {
    const standardizedValue = getStandardTime(value);
    const hours = parseInt(Math.floor(standardizedValue / SECONDS_IN_HOUR).toString(), 10);
    const minutes = parseInt(Math.floor((standardizedValue - (hours * SECONDS_IN_HOUR)) / SECONDS_IN_MINUTE).toString(), 10);
    const seconds = parseInt((standardizedValue - (hours * SECONDS_IN_HOUR) - (minutes * SECONDS_IN_MINUTE)).toString(), 10);
    const strHour = hours < 10 ? ('0' + hours) : hours;
    const strMinutes = minutes < 10 ? ('0' + minutes) : minutes;
    const strSecond = seconds < 10 ? ('0' + seconds) : seconds;
    return strHour + ':' + strMinutes + ':' + strSecond;
};

const getFormattedPercent = (value: string | number, isFirstTime = false) => {
    const validNumber = getNumberOrZero(value);
    if (isFirstTime) return (validNumber * 100).toFixed(2);
    return validNumber.toFixed(2);
};

const getFormattedNumber = (value: string | number) => getNumberOrZero(value).toString();

const getFormattedMinute = (value: string | number, isFirstTime = false) => {
    const isValidNumber = isNumber(value);
    if (!isValidNumber) return null;
    if(isFirstTime) return (+value / SECONDS_IN_MINUTE).toString();
    return value.toString();
};

const getFormattedHour = (value: string | number, isFirstTime = false) => {
    const isValidNumber = isNumber(value);
    if (!isValidNumber) return null;
    if(isFirstTime) return (((+value / SECONDS_IN_HOUR) * 100) / 100).toString();
    return value.toString();
};

const conversionFormatterFunctions = {
    1: getFormattedNumber, // second
    2: getFormattedMinute, // minute
    3: getFormattedHour, // hour
    4: getFormattedTime, // time
    5: getFormattedPercent, // percent
};

const conversionStandardFunctions = {
    1: getStandardSecond,
    2: getStandardMinute,
    3: getStandardHour,
    4: getStandardTime,
    5: getStandardPercent,
};


@Pipe({
    name: 'customMetricFormatter'
})
export class CustomMetricFormatterPipe implements PipeTransform {

    transform(value: number | string, conversionType: string, isFirstTime = false): string {
        const conversionKey = getNumberOrZero(conversionType) as keyof typeof conversionFormatterFunctions;
        if (!conversionFormatterFunctions[conversionKey]) return value.toString();
        return conversionFormatterFunctions[conversionKey](value, isFirstTime);
    }

    getStandardizedValue(value: number | string, conversionType: string) {
        const conversionKey = getNumberOrZero(conversionType) as keyof typeof conversionStandardFunctions;
        if (!conversionStandardFunctions[conversionKey]) return value;
        return conversionStandardFunctions[conversionKey](value);
    }
}
